/**
 * @typedef {import('vue-router').RouteRecordRaw} RouteRecordRaw
 */

/**
 * @import {RouteMenu,RouteMeta} from "./type.jsdoc.js"
 */

/**
 * 创建路由项菜单
 * @param {RouteMenu[]} root - 菜单项列表
 * @param {RouteMeta} meta - 菜单项定义数据
 * @returns {RouteMenu[]}
 */
export const createRouteMenu = (root, meta) => {
    const { menu: menus, menuIcons, action = '', path = '', menuOrder = 0, target = '' } = meta || {};
    if (!menus || !Array.isArray(menus) || menus.length === 0) return root;
    const title = menus[menus.length - 1];
    let menuIcon = '';
    if (menuIcons && typeof menuIcons === 'string') {
        menuIcon = menuIcons;
    } else if (!!menuIcons && Array.isArray(menuIcons)) {
        if (menuIcons.length === 1) {
            menuIcon = menuIcons[0];
        } else if (menuIcons.length === menus.length) {
            menuIcon = menuIcons[menus.length - 1];
        }
    }
    const paths = [];
    if (menus.length > 1) {
        paths.push(...menus.slice(0, -1));
    }
    let parent = null;
    if (paths.length > 0) {
        let entryItems = root;
        for (let i = 0; i < paths.length; i++) {
            let levelParent = null;
            for (let j = 0; j < entryItems.length; j++) {
                if (entryItems[j].title === paths[i]) {
                    levelParent = entryItems[j];
                    break;
                }
            }
            if (!levelParent) {
                // 找不到时创建
                const icon = !!menuIcons && Array.isArray(menuIcons) && menuIcons.length > i ? menuIcons[i] : '';
                levelParent = {
                    title: paths[i],
                    icon: icon,
                    children: [],
                    order: 0,
                };
                entryItems.push(levelParent);
            }
            if (!levelParent.children) {
                levelParent.children = [];
            }
            parent = levelParent;
            entryItems = levelParent.children;
        }
    }
    let menuAction = '';
    let menuHref = '';
    if (action) {
        menuAction = action;
    } else if (path) {
        menuHref = path;
    }
    if (!menuAction && !menuHref) {
        return root;
    }
    /** @type {RouteMenu} */
    const menuItem = {
        title: title,
        icon: menuIcon,
        href: menuHref,
        target,
        action: menuAction,
        order: menuOrder || 0,
    };
    if (parent) {
        parent.children.push(menuItem);
    } else {
        root.push(menuItem);
    }
    return root;
};

/**
 * 根据路由配置生成vue-router路由项
 * @param {string} app
 * @param {RouteMeta} meta
 * @returns {RouteRecordRaw|null}
 */
export const createRoute = (app, meta) => {
    const {
        title,
        path,
        redirect,
        page,
        layout,
        visible,
        closable,
        props,
        roles,
        rules,
    } = meta || {};
    if (!page || (!path && !redirect)) return null;

    const routeProps = props ? (r) => ({ ...r.query, ...r.params }) : false;

    /** @type {RouteRecordRaw} */
    const routeRaw = {
        path,
        redirect,
        component: page,
        props: routeProps,
        meta: {
            title,
            history: visible,
            closable,
            group: app,
            roles: roles || [],
            rules: rules || [],
        },
    };
    if (layout) {
        if (Array.isArray(layout) && layout.length > 1) {
            /** @type {RouteRecordRaw} */
            let lastLayout = {
                path: '',
                component: layout[0],
                children: [],
            };
            for (let i = 1; i < layout.length; i++) {
                const curLayout = {
                    path: '',
                    component: layout[i],
                    children: [],
                };
                lastLayout.children.push(curLayout);
                lastLayout = curLayout;
            }
            lastLayout.children.push(routeRaw);
            return lastLayout;
        }
        // 只有一层
        return {
            path: '',
            component: Array.isArray(layout) ? layout[0] : layout,
            children: [ routeRaw ],
        };
    }
    // 无布局层
    return routeRaw;
};

/**
 * 根据路由配置生成路由列表
 * @param {string} app
 * @param {RouteMeta[]||undefined} metas
 * @returns {RouteRecordRaw[]}
 */
export const createRoutes = (app, metas) => {
    const routes = [];
    if (!metas || !Array.isArray(metas) || metas.length === 0) {
        return routes;
    }
    for (const meta of metas) {
        const route = createRoute(app, meta);
        if (route) {
            routes.push(route);
        }
    }
    return routes;
};

/**
 * 根据路由配置创建路由菜单及路由
 * @param {string} app
 * @param {RouteMeta[]|null} [metas=[]]
 * @returns {{routes: RouteRecordRaw[], menus: RouteMenu[] }}
 */
export const createMenuAndRoutes = (app, metas = []) => {
    /** @type {RouteMenu[]} */
    let rootMenus = [];

    /** @type {RouteRecordRaw[]} */
    const routes = [];

    if (!metas || !Array.isArray(metas)) {
        return { routes, menus: rootMenus };
    }

    for (const meta of metas) {
        const {
            menu,
        } = meta;
        if (menu) {
            rootMenus = createRouteMenu(rootMenus, meta);
        }
        const route = createRoute(app, meta);
        if (route) {
            routes.push(route);
        }
    }
    return { routes, menus: rootMenus };
};
